import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import theme from "../../styles/theme";
import axios from "axios";
import {
  getSelectedText,
  removeTextAndAddNewContent,
  putRedlinesOffice,
  stripHtmlAndSpecialChars,
} from "../../actions/prompt";
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../reducers/user";
import { authorizeUser, selectIsCreditExceededError } from "../../reducers/auth";
import { UserAlertComponent } from "../../components/UsageAlert/UsageAlertComponent";
import { toast } from "react-toastify";
import { ITerm } from "../../interfaces/ITermsInterface";
import { IMessage } from "../../../utilities/types";
import { SELECTED_TEXT_CHARACTER_LIMIT } from "../../../utilities/constants";
import TermLink from "../../components/TermLink";
import JsxParser from "react-jsx-parser";
import TermModal from "../../../utilities/TermModal";
import { environment_conf } from "../../../utilities/environment";
import Redline from "../../components/WriterSearch/Redline";
import {
  getDocumentSegmentedData,
  getDocumentSpecificNamespace,
  getTermsWords,
  ingestDoc,
} from "../../../utilities/utils";
import { useAnalysis } from "../../hooks/useAnalysis";
import { Button } from "../../components/ui/Button";
import { LinkButton } from "../../components/ui/LinkButton";
import { useChat } from "../../hooks/useChat";
import PromptInput from "../../components/PromptInput";
import InfoBox from "../../components/InfoBox/InfoBox";
import {
  getModelId,
  getApiModelName,
} from "../../../utilities/providerService";
import { ProviderMapper } from "../../../utilities/mappers/ProviderMapper";
import { axiosInstance } from "../../../utilities/axios/axiosInstance";
import { CREDIT_EXCEED_ERROR_CODE, CREDIT_EXCEED_ERROR_CODE as CREDIT_EXCEED_ERROR_STATUS_CODE } from "../../../utilities/errors/ErrorStatusCodes";
import { CREDIT_EXCEED_ERROR_CODE_ERROR_MESSAGE } from "../../../utilities/errors/ErrorMessageByForStatusCode";
import { LICENSE_TYPES } from "../../interfaces/IUserInterface";

const extractNewTerms = (text, currentTerms) => {
  const terms = new Set();

  const strippedText = text
    .replace(/<h[1-6]>.*?<\/h[1-6]>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replaceAll('"', '"')
    .replaceAll('"', '"')
    .replaceAll(",", "")
    .trim();

  // Helper function to check if a term appears independently in the text
  const isIndependentTerm = (term) => {
    const escapedTerm = term.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const standAlonePattern = new RegExp(`\\b${escapedTerm}\\b`, "g");
    const matches = [...strippedText.matchAll(standAlonePattern)];

    return matches.some((match) => {
      const startPos = match.index;
      const endPos = startPos + term.length;

      return !currentTerms.some((currentTerm) => {
        const termPattern = new RegExp(
          `\\b${currentTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
          "g"
        );
        const termMatches = [...strippedText.matchAll(termPattern)];

        return termMatches.some((termMatch) => {
          const termStart = termMatch.index;
          const termEnd = termStart + currentTerm.length;
          return startPos >= termStart && endPos <= termEnd;
        });
      });
    });
  };

  const segments = strippedText.split(/[.:]|\n/); // Split by periods, colons, and newlines

  segments.forEach((segment) => {
    // Process the segment to find consecutive capitalized words
    const words = segment.trim().split(/\s+/);
    let potentialTerm = [];

    for (let i = 0; i < words.length; i++) {
      const word = words[i].trim();

      // Check if word starts with capital letter
      if (/^[A-Z]/.test(word)) {
        potentialTerm.push(word);
      } else if (potentialTerm.length > 0) {
        const term = potentialTerm.join(" ").trim();

        if (!ignoreTerm(term) && isIndependentTerm(term)) {
          terms.add(term);
        }
        potentialTerm = [];
      }
    }

    // Handle last term in segment
    if (potentialTerm.length > 0) {
      const term = potentialTerm.join(" ").trim();
      if (!ignoreTerm(term) && isIndependentTerm(term)) {
        terms.add(term);
      }
    }

    // Also look for quoted terms
    const quotedTerms = segment.match(/"([^"]+)"/g) || [];
    quotedTerms.forEach((quotedTerm) => {
      const term = quotedTerm.replace(/^"|"$/g, "").trim();
      if (/^[A-Z]/.test(term) && !ignoreTerm(term) && isIndependentTerm(term)) {
        terms.add(term);
      }
    });
  });

  const extractedTerms = Array.from(terms);
  return extractedTerms;
};

// Helper function to ignore common words
function ignoreTerm(term) {
  const ignoreWords = [
    "and",
    "or",
    "include",
    "includes",
    "including",
    "any",
    "this",
    "without limitation",
    "will",
    "shall",
    "herein",
    "within",
    "in",
    "section",
    "hereof",
    "hereby",
    "hereto",
    "hereunder",
    "from",
    "from and including",
    "to",
    "until",
    "if",
    "as",
    "through",
    "as is",
    "the",
    "The",
    "you",
    "we",
  ];

  if (ignoreWords.includes(term.toLowerCase())) return true;
  if (term.length < 2) return true;
  if (/\d/.test(term) && !term.includes(" ")) return true;

  return false;
}

// Helper function to create regex-enhanced items
const createRegexItems = (terms: ITerm[], sections: ITerm[]) => {
  return [...terms, ...sections]
    .sort((a, b) => b.word.length - a.word.length)
    .map((item) => {
      const word = new RegExp(
        `\\b${item.word.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
        "i"
      );
      const aliases =
        (item as any)?.aliases?.map(
          (alias) =>
            new RegExp(
              `\\b${alias.word.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
              "i"
            )
        ) || [];

      return {
        ...item,
        regex: [word, ...aliases],
      };
    });
};

const findMatches = (
  part: string,
  items: Array<ITerm & { regex: RegExp[] }>,
  newTerms: string[]
) => {
  const matchMap = new Map<
    number,
    {
      end: number;
      term: ITerm | null;
      text: string;
    }
  >();

  // Helper function to check for overlaps
  const hasOverlap = (start: number, end: number) => {
    let isOverlapping = false;
    matchMap.forEach((existing, existingStart) => {
      if (
        (start >= existingStart && start < existing.end) ||
        (end > existingStart && end <= existing.end) ||
        (start <= existingStart && end >= existing.end)
      ) {
        isOverlapping = true;
      }
    });
    return isOverlapping;
  };

  // First find matches for existing terms - case insensitive
  items.forEach(({ regex, ...item }) => {
    regex.forEach((regexPattern) => {
      const pattern = new RegExp(`\\b${regexPattern.source}\\b[:;,.]?`, "gi");
      let match;

      while ((match = pattern.exec(part)) !== null) {
        const start = match.index;
        const cleanText = match[0].replace(/[:;,.]$/, "");
        const end = start + cleanText.length;

        // Only add if no overlap and not part of a longer existing match
        if (!hasOverlap(start, end)) {
          matchMap.set(start, {
            end,
            term: item,
            text: cleanText,
          });
        }
      }
    });
  });

  // Then find matches for new terms - case sensitive
  newTerms.forEach((newTerm) => {
    const escapedTerm = newTerm
      .replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
      .replace(/\s+/g, "\\s+");

    let pattern = new RegExp(`(?:")?\\b${escapedTerm}\\b(?:")?`, "g");
    let match;

    while ((match = pattern.exec(part)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      if (!hasOverlap(start, end)) {
        matchMap.set(start, {
          end,
          term: null,
          text: match[0].replace(/^"|"$/g, "").trim(),
        });
      }
    }
  });

  return Array.from(matchMap.entries()).sort(([posA], [posB]) => posB - posA);
};

const processTextPart = (
  part: string,
  items: Array<ITerm & { regex: RegExp[] }>,
  newTerms: string[],
  setCurrentTerm: (termId: string) => void
) => {
  if (/<[^>]+>|&[^;]+;/.test(part)) {
    return part;
  }

  // Pre-process the part to normalize quotes and whitespace
  const normalizedPart = part
    .replace(/[""]|"/g, '"')
    .replace(/\s+/g, " ")
    .trim();

  const sortedMatches = findMatches(normalizedPart, items, newTerms);
  let processedPart = normalizedPart;

  // Process matches from end to start to avoid position shifting
  for (const [start, match] of sortedMatches) {
    const word = match.text
      .replace(/^"|"$/g, "") // remove surrounding quotes
      .trim();

    const termLink =
      match.term === null
        ? `<TermLink termId={null}>${word}</TermLink>`
        : `<TermLink setCurrentTerm={setCurrentTerm} termId="${match.term.id}">${word}</TermLink>`;

    processedPart =
      processedPart.slice(0, start) + termLink + processedPart.slice(match.end);
  }

  return processedPart;
};

export const hyperlinkTermsAndSections = (
  text: string,
  terms: ITerm[],
  newTerms: string[],
  sections: ITerm[],
  articles: ITerm[],
  setCurrentTerm: (termId: string) => void
) => {
  try {
    const regexItems = createRegexItems(terms, [...sections, ...articles]);

    // Optimize for large responses by limiting processing if text is very long
    if (text.length > 50000) {
      console.warn("Large response detected, processing in chunks");
    }

    const elements = text
      .split(/(<[^>]+>|&[^;]+;)/)
      .map((part) =>
        processTextPart(part, regexItems, newTerms, setCurrentTerm)
      );

    return (
      <JsxParser
        bindings={{ setCurrentTerm }}
        jsx={elements.join("")}
        components={{ TermLink }}
      />
    );
  } catch (error) {
    console.error("Error processing hyperlinks:", error);
    // Fallback to plain HTML rendering if hyperlink processing fails
    return <div dangerouslySetInnerHTML={{ __html: text }} />;
  }
};

type Compare = {
  suggestedText: string;
  originalText: string;
};

const Prompt = () => {
  const dispatch = useDispatch<any>();
  const { analysis } = useAnalysis();
  const user = useSelector(selectUser);
  const isCreditExceeded = useSelector(selectIsCreditExceededError);
  const {
    messages,
    addMessage,
    clearChat,
    setChat,
    setNamespace,
    removeLastMessage,
    namespace,
  } = useChat();


  useEffect(() => {
    if (user.accessToken !== '') {
      dispatch(authorizeUser());
    }
  }, [dispatch]);


  const { aiModel, aiProvider, accessToken } = user;

  const [loading, setLoading] = useState(false);
  const [insertingDocument, setInsertingDocument] = useState(false);

  const [currentTerm, setCurrentTerm] = useState<string>();
  const [compare, setCompare] = useState<Compare>(null);

  const [newTerms, setNewTerms] = useState<string[]>([]);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const terms = analysis.clauses.filter((r) => r.clauseType === "TERM");
  const sections = analysis.clauses.filter((r) => r.clauseType === "SECTION");
  const articles = analysis.clauses.filter((r) => r.clauseType === "ARTICLE");

  const promptTextarea = useRef<HTMLTextAreaElement>(null);

  const openAiPrompt = async (prompt: string) => {
    setLoading(true);
    setCompare(null);
    setNewTerms([]);

    const selectedText = await getSelectedText();

    var prompt = prompt;

    if (selectedText.length > SELECTED_TEXT_CHARACTER_LIMIT) {
      toast.error(
        "Please select less than 10000 characters at a time to allow us to best process your messages",
        {
          position: toast.POSITION.BOTTOM_LEFT,
        }
      );
      setLoading(false);
      return;
    }

    let question = "";

    const isEmpty = prompt.trim().length < 1;

    if (isEmpty && selectedText.trim() != "") {
      // User has selected a text but haven't provided any input
      prompt = "What does the highlighted text mean?";
      question = `What does the highlighted text mean: #${selectedText}# `;
    } else if (!isEmpty && selectedText.trim() != "") {
      // User has selected some text and is asking a question regarding that
      question = `${prompt} Please use the following information about the contract to aid your response: #${selectedText}#.`;
    } else if (isEmpty && selectedText.trim() == "") {
      // User has not selected nor is asking any question
      toast.error("Please input some messages.", {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      setLoading(false);
      return;
    } else {
      // User is asking a question
      question = prompt;
    }

    addMessage({
      username: user.name === "" ? "You" : user.name,
      content: prompt,
      created: new Date().toISOString(),
      type: "user",
    });

    try {
      const modelId = await getModelId(aiModel);

      if (!modelId) {
        toast.error(`Failed to retrieve modelId for aiModel: ${aiModel}`, {
          position: toast.POSITION.BOTTOM_LEFT,
        });
        setLoading(false);
        return;
      }

      const stringModelId = modelId.toString();
      const allTermsTitle = await getTermsWords();
      const apiModelName = await getApiModelName(aiModel);
      if (!validateAIProvider(aiProvider)) {
        setLoading(false);
        return;
      }
      if (!validateApiModelName(apiModelName)) {
        setLoading(false);
        return;
      }
      const providerMapper = new ProviderMapper();
      const identityProvider = providerMapper.mapProviderToEnum(user.provider);
      const response = await axiosInstance.post<{
        output: string;
        consideratons: string[];
      }>(
        `${environment_conf.base_llm_domain}/query`,
        {
          question: question,
          modelName: apiModelName,
          provider: aiProvider,
          messages: messages.map((m) => ({ type: m.type, content: m.content })),
          terms: allTermsTitle,
          namespace: namespace,
          modelId: stringModelId,
          masterUserId: user.id.toString(),
          identityProvider: identityProvider,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const langchainOutput = response.data.output.replace(
        /^<h[1-6]>.*?<\/h[1-6]>/m,
        ""
      );
      // Check if response appears to be truncated
      if (
        langchainOutput.endsWith("...") ||
        (!langchainOutput.trim().endsWith(".") &&
          !langchainOutput.trim().endsWith("</li>") &&
          !langchainOutput.trim().endsWith("</ul>"))
      ) {
        console.warn("Response may be truncated");
      }

      const revisedContent = langchainOutput;

      // Check if prompt suggests generating new content
      const guardrialIndicators = [
        "suggest",
        "create new",
        "give me",
        "recommend",
        "provide",
        "generate",
        "make",
        "define new",
        "write",
        "draft",
        "propose",
      ];

      // Convert prompt to lowercase for case-insensitive matching
      const promptLower = prompt.toLowerCase();

      // Check if prompt contains any suggestive words
      const isSuggestive = guardrialIndicators.some((word) =>
        promptLower.includes(word.toLowerCase())
      );

      let nt = [];

      if (isSuggestive) {
        nt = extractNewTerms(langchainOutput, allTermsTitle);
        setNewTerms(nt ?? []);
      }

      let renderContentSections;
      try {
        renderContentSections = hyperlinkTermsAndSections(
          revisedContent,
          terms,
          nt ?? [],
          sections,
          articles,
          setCurrentTerm
        );
      } catch (error) {
        console.error("Failed to process hyperlinks, using fallback:", error);
        renderContentSections = (
          <div dangerouslySetInnerHTML={{ __html: revisedContent }} />
        );
      }

      addMessage({
        username: "Spectacles",
        content: revisedContent,
        renderContent: renderContentSections,
        created: new Date().toISOString(),
        type: "assistant",
      });

      // Scroll to bottom to show the full message
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } catch (error) {
      toast.error(`${error}`);

      removeLastMessage();

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status) {
          if (status === 400 && data?.type === CREDIT_EXCEED_ERROR_STATUS_CODE) {
            if (user.licenseType === LICENSE_TYPES.INDIVIDUAL) {
              toast.error(CREDIT_EXCEED_ERROR_CODE_ERROR_MESSAGE, {
                position: toast.POSITION.BOTTOM_LEFT,
              });
            }
          } else {
            toast.error(data?.message || data?.detail || "An unknown error occurred", {
              position: toast.POSITION.BOTTOM_LEFT,
            });
          }
        } else {
          // Network / no response
          toast.error(error.message || data?.detail || "Network error occurred", {
            position: toast.POSITION.BOTTOM_LEFT,
          });
        }
      } else {
        // Non-Axios errors
        console.error(error);
        toast.error(String(error) || "Unexpected error", {
          position: toast.POSITION.BOTTOM_LEFT,
        });
      }
    } finally {
      dispatch(authorizeUser());
      setLoading(false);
    }
  };

  const validateAIProvider = (aiProvider: any): boolean => {
    if (
      aiProvider === null ||
      aiProvider === undefined ||
      aiProvider === "" ||
      aiProvider === "Please Select AI Provider"
    ) {
      toast.error("Please select an AI provider to continue.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    return true;
  };

  const validateApiModelName = (apiModelName: any): boolean => {
    if (apiModelName === undefined) {
      toast.error("No AI model selected. Please choose a model to continue.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    return true;
  };

  const onInsert = async (content: string) => {
    // await applyStrikethrough();
    // await insertContentNextSelected(content);
    const strippedContent = stripHtmlAndSpecialChars(content);
    await removeTextAndAddNewContent(strippedContent);
  };

  const onInsertCompare = async (content: string) => {
    //await putRedlines(content);
    const a = await putRedlinesOffice(content);
  };

  const onCompare = async (aiOutput: string) => {
    const selectedText = await getSelectedText();

    if (!selectedText) {
      toast.error("Please select some text to compare against");
      return;
    }

    const strippedContent = stripHtmlAndSpecialChars(aiOutput);

    setCompare({
      originalText: selectedText,
      suggestedText: strippedContent,
    });
  };

  const onSubmit = async () => {
    setInsertingDocument(true);

    clearChat();
    setCompare(null);

    try {
      if (!validateAIProvider(aiProvider)) {
        setInsertingDocument(false);
        return;
      }
      const providerMapper = new ProviderMapper();
      const { data } = await ingestDoc(
        user.id,
        aiProvider,
        accessToken,
        providerMapper.mapProviderToEnum(user.provider)
      );
      setNamespace(data.namespace);
      toast.info("Data Ingested, start chatting away!");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status) {
          if (status === 400 && data?.type === CREDIT_EXCEED_ERROR_STATUS_CODE) {
            if (user.licenseType === LICENSE_TYPES.INDIVIDUAL) {
              toast.error(CREDIT_EXCEED_ERROR_CODE_ERROR_MESSAGE, {
                position: toast.POSITION.BOTTOM_LEFT,
              });
            }
          } else {
            toast.error(data?.message || data?.detail || "An unknown error occurred", {
              position: toast.POSITION.BOTTOM_LEFT,
            });
          }
        } else {
          // Network / no response
          toast.error(error.message || data?.detail || "Network error occurred", {
            position: toast.POSITION.BOTTOM_LEFT,
          });
        }
      } else {
        // Non-Axios errors
        console.error(error);
        toast.error(String(error) || "Unexpected error", {
          position: toast.POSITION.BOTTOM_LEFT,
        });
      }
    } finally {
      dispatch(authorizeUser());
      setInsertingDocument(false);
    }
  };

  return (
    isCreditExceeded ? <UserAlertComponent /> : (
      <div className="Ai-Vision">
        <Button onClick={onSubmit}>
          {insertingDocument ? `Ingesting...` : `Ingest Doc`}
        </Button>

        {messages.length < 1 ? <InfoBox /> : null}

        <div className="messages">

          {messages.map((message, index) => {
            return (
              <div
                className="message"
                key={index}
                ref={index === messages.length - 1 ? messagesEndRef : null}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    textAlign: message.type === "user" ? "right" : "left",
                    marginBottom: "8px",
                  }}
                >
                  {message.type === "assistant" ? (
                    <span style={{ marginRight: "10px" }}>
                      <img
                        width="32"
                        height="32"
                        src="/assets/logo-filled.png"
                        alt="Spectacles Logo"
                      />
                    </span>
                  ) : (
                    <strong className="username">{message.username}</strong>
                  )}
                  <p style={{ font: "bold" }}>
                    {new Intl.DateTimeFormat("default", {
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(message.created))}
                  </p>
                </div>
                <div
                  className="message-content"
                  style={{
                    backgroundColor:
                      message.type === "user" ? theme.pallet.primary : "whitesmoke",
                    color: message.type === "user" && "white",
                  }}
                >
                  {message.renderContent ? (
                    message.renderContent
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: message.content }} />
                  )}
                </div>
                {message.username === "Spectacles" && (
                  <div style={{ display: "flex" }}>
                    <div style={{ marginRight: "8px" }} className="insertBtn">
                      <button onClick={() => onInsert(message.content)}>
                        Insert
                      </button>
                    </div>
                    <div className="insertBtn">
                      <button onClick={() => onCompare(message.content)}>
                        Compare
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {newTerms.length ? (
            <div className="message-considerations">
              <p style={{ marginBottom: "6px" }}>
                It seems as though the following new terms have been added in the
                AIs response:
              </p>
              {newTerms.map((c) => (
                <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      textDecoration: "underline",
                      marginBottom: "1px",
                    }}
                  >
                    {c}
                  </div>
                  <button
                    style={{
                      color: "green",
                      textDecoration: "underline",
                      fontSize: "11px",
                    }}
                    onClick={() => {
                      openAiPrompt(
                        `Create definition for new term you just added to the recent output "${c}"`
                      );
                    }}
                  >
                    Generate
                  </button>
                  <button
                    style={{
                      color: "red",
                      textDecoration: "underline",
                      fontSize: "11px",
                    }}
                    onClick={() => setNewTerms(newTerms.filter((t) => t !== c))}
                  >
                    Remove
                  </button>
                </p>
              ))}
              <LinkButton
                style={{ marginTop: "6px", fontSize: "14px", fontWeight: "bold" }}
                onClick={() => {
                  openAiPrompt(
                    `Create definitions for each of the new terms you just added to the recent output, "${newTerms.join(
                      ", "
                    )}"`
                  );
                }}
              >
                Generate defintions for these new terms
              </LinkButton>
            </div>
          ) : null}
          {compare && (
            <div className="message-redline">
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button onClick={() => setCompare(null)}>Close</button>
                <button onClick={() => onInsertCompare(compare.suggestedText)}>
                  Insert
                </button>
              </div>
              <Redline
                originalText={compare.originalText}
                suggestedText={compare.suggestedText}
              />
            </div>
          )}
        </div>

        <PromptInput
          textareaRef={promptTextarea}
          isLoading={loading}
          clearChat={clearChat}
          aiPrompt={openAiPrompt}
        />

        {currentTerm && (
          <TermModal id={currentTerm} setCurrentTerm={setCurrentTerm} />
        )}
      </div>
    )
  );
};

export default Prompt;
